import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Stethoscope, Calendar, CheckCircle, XCircle, IndianRupee, Clock, UserX } from "lucide-react";
import { api, formatINR } from "@/lib/api";

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [tab, setTab] = useState("pending");
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [appts, setAppts] = useState([]);

  const load = async () => {
    const [m, d, p, a] = await Promise.all([
      api.get("/admin/metrics"),
      api.get("/admin/doctors"),
      api.get("/admin/patients"),
      api.get("/admin/appointments"),
    ]);
    setMetrics(m.data); setDoctors(d.data); setPatients(p.data); setAppts(a.data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => { await api.patch(`/admin/doctors/${id}/approve`); toast.success("Doctor approved"); load(); };
  const reject  = async (id) => { await api.patch(`/admin/doctors/${id}/reject`);  toast.success("Doctor rejected"); load(); };

  const pending = doctors.filter((d) => d.status === "pending");
  const approved = doctors.filter((d) => d.status === "approved");

  const Stat = ({ icon: I, label, value, color }) => (
    <div className="card p-5">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}><I className="h-5 w-5" /></div>
      <div className="mt-4 text-2xl font-extrabold text-slate-900">{value}</div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Super Admin</h1>
        <p className="mt-1 text-sm text-slate-600">Platform overview and controls.</p>
      </div>

      {metrics && (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <Stat icon={Users} label="Patients" value={metrics.total_patients} color="bg-blue-50 text-blue-700" />
          <Stat icon={Stethoscope} label="Doctors" value={metrics.total_doctors} color="bg-teal-50 text-teal-700" />
          <Stat icon={Clock} label="Pending Approvals" value={metrics.pending_doctors} color="bg-amber-50 text-amber-700" />
          <Stat icon={Calendar} label="Appointments" value={metrics.total_appointments} color="bg-indigo-50 text-indigo-700" />
          <Stat icon={CheckCircle} label="Completed" value={metrics.completed} color="bg-emerald-50 text-emerald-700" />
          <Stat icon={IndianRupee} label="Revenue" value={formatINR(metrics.revenue)} color="bg-fuchsia-50 text-fuchsia-700" />
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-2 border-b border-slate-200">
        {[
          { id: "pending", label: `Pending Doctors (${pending.length})` },
          { id: "approved", label: `All Doctors (${approved.length})` },
          { id: "patients", label: `Patients (${patients.length})` },
          { id: "appts", label: `Appointments (${appts.length})` },
        ].map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} className={`relative px-4 py-3 text-sm font-semibold ${tab === t.id ? "text-blue-700" : "text-slate-500 hover:text-slate-700"}`}>
            {t.label}
            {tab === t.id && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-blue-700" />}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto">
        {tab === "pending" && (
          pending.length === 0 ? <div className="card p-10 text-center text-sm text-slate-500">No pending doctors.</div> :
          <div className="space-y-3">
            {pending.map((d) => (
              <div key={d.id} className="card flex flex-wrap items-center justify-between gap-3 p-5">
                <div className="flex items-center gap-4">
                  <img src={d.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(d.name)}`} alt={d.name} className="h-14 w-14 rounded-xl object-cover" />
                  <div>
                    <div className="font-bold text-slate-900">{d.name}</div>
                    <div className="text-xs text-blue-700">{d.specialty} • {d.qualification}</div>
                    <div className="mt-0.5 text-xs text-slate-500">Reg: {d.registration_no} • {d.city}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`approve-${d.id}`} onClick={() => approve(d.id)} className="btn-secondary !py-2 !text-sm"><CheckCircle className="h-4 w-4" /> Approve</button>
                  <button data-testid={`reject-${d.id}`} onClick={() => reject(d.id)} className="btn-outline !py-2 !text-sm text-rose-700 hover:bg-rose-50"><XCircle className="h-4 w-4" /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "approved" && (
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Specialty</th><th className="px-4 py-3">City</th><th className="px-4 py-3">Fee</th><th className="px-4 py-3">Rating</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {approved.map((d) => (
                  <tr key={d.id}><td className="px-4 py-3 font-semibold">{d.name}</td><td className="px-4 py-3">{d.specialty}</td><td className="px-4 py-3">{d.city}</td><td className="px-4 py-3">{formatINR(d.consultation_fee)}</td><td className="px-4 py-3">{d.rating?.toFixed(1) || "—"}</td><td className="px-4 py-3"><span className="badge bg-emerald-50 text-emerald-800">{d.status}</span></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "patients" && (
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Joined</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {patients.map((p) => (
                  <tr key={p.id}><td className="px-4 py-3 font-semibold">{p.name}</td><td className="px-4 py-3">{p.email}</td><td className="px-4 py-3">{p.phone || "—"}</td><td className="px-4 py-3">{p.created_at?.slice(0,10)}</td></tr>
                ))}
                {patients.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-slate-500">No patients yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "appts" && (
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                <tr><th className="px-4 py-3">Booking ID</th><th className="px-4 py-3">Doctor</th><th className="px-4 py-3">Patient</th><th className="px-4 py-3">Date & Time</th><th className="px-4 py-3">Fee</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {appts.map((a) => (
                  <tr key={a.id}><td className="px-4 py-3 font-mono text-xs">#{a.booking_id}</td><td className="px-4 py-3">{a.doctor_name}</td><td className="px-4 py-3">{a.patient_name}</td><td className="px-4 py-3">{a.date} • {a.time}</td><td className="px-4 py-3">{formatINR(a.fee)}</td><td className="px-4 py-3"><span className="badge bg-slate-100 text-slate-700">{a.status}</span></td></tr>
                ))}
                {appts.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500">No appointments.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
